import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import UserDashboardLayout from '../../components/dashboard/UserDashboardLayout';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import api, { getImageUrl } from '../../utils/api';

interface Property {
    _id: string;
    title: string;
    price: number;
    city: string;
    area: string;
    status: string;
    type: string;
    images: string[];
    isActive: boolean;
    isFeatured: boolean;
}

export default function BrokerMyProjects() {
    const { token } = useAuth();
    const { theme } = useTheme();
    const [properties, setProperties] = useState<Property[]>([]);
    const [loading, setLoading] = useState(true);
    const [deleting, setDeleting] = useState<string | null>(null);
    const [msg, setMsg] = useState<{ type: 'success' | 'error', text: string } | null>(null);

    const fetchProperties = () => {
        if (!token) { setLoading(false); return; }
        setLoading(true);
        api.getMyProperties(token)
            .then(data => {
                if (Array.isArray(data)) setProperties(data);
            })
            .catch(console.error)
            .finally(() => setLoading(false));
    };

    useEffect(() => { fetchProperties(); }, [token]);

    const handleDelete = async (id: string) => {
        if (!token || !window.confirm('Are you sure you want to delete this project?')) return;
        setDeleting(id);
        try {
            const res = await api.deleteProperty(token, id);
            if (res.message === 'Property deleted successfully') {
                setProperties(prev => prev.filter(p => p._id !== id));
                setMsg({ type: 'success', text: 'Project removed from your portfolio.' });
            } else {
                setMsg({ type: 'error', text: res.message || 'Failed to delete project.' });
            }
        } catch {
            setMsg({ type: 'error', text: 'Server error. Please try again.' });
        }
        setDeleting(null);
    };

    return (
        <UserDashboardLayout>
            <div className="max-w-7xl mx-auto py-8">
                {/* Page Header */}
                <div className="flex flex-col md:flex-row items-center justify-between gap-8 mb-12 px-4 animate__animated animate__fadeInDown">
                    <div>
                        <h1 className="text-4xl md:text-5xl font-black text-[var(--text-primary)] mb-3 tracking-tighter uppercase italic">Asset Portfolio</h1>
                        <p className="text-[var(--text-secondary)] font-black text-xs uppercase tracking-[0.2em] opacity-70">Monitor your active listings and real-time performance analytics.</p>
                    </div>
                    <Link 
                        to="/broker-add-project" 
                        className="btn-purple px-8 py-5 no-underline group shadow-2xl flex items-center gap-4 animate__animated animate__pulse animate__infinite animate__slow"
                    >
                        <i className="fa-solid fa-plus-circle text-lg"></i>
                        Launch New Project
                    </Link>
                </div>

                {/* Message Alert */}
                {msg && (
                    <div className={`mx-4 p-6 rounded-[2.5rem] mb-10 flex items-center gap-4 border animate__animated animate__shakeX ${
                        msg.type === 'success' 
                        ? 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20' 
                        : 'bg-rose-500/10 text-rose-500 border-rose-500/20'
                    }`}>
                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${msg.type === 'success' ? 'bg-emerald-500 text-white' : 'bg-rose-500 text-white shadow-lg'}`}>
                            <i className={`fa-solid ${msg.type === 'success' ? 'fa-circle-check' : 'fa-circle-exclamation'} text-lg`}></i>
                        </div>
                        <span className="font-black uppercase tracking-widest text-xs italic">{msg.text}</span>
                    </div>
                )}

                {/* Content Area */}
                <div className="glass-card overflow-hidden mx-4 animate__animated animate__fadeIn">
                    <div className="p-10 border-b border-[var(--purple)]/10 bg-[var(--purple)]/5 flex justify-between items-center relative overflow-hidden">
                        <div className="absolute top-0 left-0 w-1 h-full bg-[var(--purple)]"></div>
                        <h3 className="text-xs font-black text-[var(--text-secondary)] uppercase tracking-[0.3em] flex items-center gap-4">
                            Operational Nodes 
                            <span className="bg-[var(--purple)] text-white px-3 py-1 rounded-lg text-[10px] shadow-lg shadow-[var(--purple)]/20 animate-pulse">{properties.length}</span>
                        </h3>
                    </div>

                    {loading ? (
                        <div className="text-center py-32">
                            <div className="w-16 h-16 border-4 border-[var(--purple)] border-t-transparent rounded-full animate-spin mx-auto mb-8 shadow-2xl"></div>
                            <p className="text-[var(--text-secondary)] font-black uppercase tracking-[0.4em] text-[10px] animate-pulse">Syncing Portfolio Stream...</p>
                        </div>
                    ) : properties.length === 0 ? (
                        <div className="text-center py-32 px-12">
                            <div className="w-28 h-28 bg-[var(--purple)]/5 text-[var(--purple)]/20 rounded-[2.5rem] flex items-center justify-center mx-auto mb-10 shadow-inner border border-[var(--purple)]/10">
                                <i className="fa-solid fa-satellite-dish text-5xl"></i>
                            </div>
                            <h2 className="text-[var(--text-primary)] font-black text-3xl mb-4 tracking-tighter uppercase italic">No Active Nodes</h2>
                            <p className="text-[var(--text-secondary)] font-bold mb-12 max-w-sm mx-auto uppercase text-[10px] tracking-widest opacity-60 italic leading-relaxed">Your professional portfolio is currently dormant. Initialize your first asset deployment to begin broadcasting.</p>
                            <Link to="/broker-add-project" className="btn-purple px-10 py-5 no-underline shadow-2xl">
                                Execute First Launch
                            </Link>
                        </div>
                    ) : (
                        <div className="divide-y divide-[var(--purple)]/10">
                            {properties.map((item, idx) => (
                                <div key={item._id} className="p-8 md:p-12 hover:bg-[var(--purple)]/5 transition-all group flex flex-col lg:flex-row items-start lg:items-center gap-10 animate__animated animate__fadeInUp" style={{ animationDelay: `${idx * 0.1}s` }}>
                                    {/* Thumbnail */}
                                    <div className="w-full lg:w-72 h-52 rounded-[2.5rem] overflow-hidden border border-[var(--purple)]/10 shadow-2xl shrink-0 relative group/img bg-[var(--bg-main)]">
                                        {item.images && item.images.length > 0 ? (
                                            <img src={getImageUrl(item.images[0])} className="w-full h-full object-cover group-hover/img:scale-110 transition-transform duration-1000" alt={item.title} />
                                        ) : (
                                            <div className="w-full h-full bg-[var(--purple)]/5 flex items-center justify-center text-[var(--purple)]/20">
                                                <i className="fa-solid fa-image text-5xl"></i>
                                            </div>
                                        )}
                                        <div className="absolute top-4 left-4">
                                            <span className="px-4 py-2 rounded-2xl bg-black/40 backdrop-blur-xl text-white text-[10px] font-black uppercase tracking-widest border border-white/20 shadow-xl">
                                                {item.type}
                                            </span>
                                        </div>
                                    </div>
                                    
                                    {/* Details */}
                                    <div className="flex-1 space-y-6">
                                        <div className="flex flex-wrap items-center gap-4">
                                            <span className={`px-5 py-2 rounded-full text-[9px] font-black uppercase tracking-[0.2em] border shadow-lg ${
                                                item.status === 'Ready to Move' 
                                                ? 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20' 
                                                : 'bg-[var(--gold)]/10 text-[var(--gold)] border-[var(--gold)]/20'
                                            }`}>
                                                <span className="inline-block w-1.5 h-1.5 rounded-full bg-current mr-2 animate-pulse"></span>
                                                {item.status}
                                            </span>
                                            {item.isFeatured && (
                                                <span className="px-5 py-2 rounded-full bg-[var(--purple)]/10 text-[var(--purple)] text-[9px] font-black uppercase tracking-[0.2em] border border-[var(--purple)]/20 flex items-center gap-2 shadow-lg">
                                                    <i className="fa-solid fa-crown text-[10px]"></i>
                                                    Premium Node
                                                </span>
                                            )}
                                        </div>
                                        
                                        <h3 className="text-3xl font-black text-[var(--text-primary)] group-hover:text-[var(--purple)] transition-colors tracking-tighter uppercase italic">
                                            <Link to={`/property-details/${item._id}`} className="no-underline text-inherit">{item.title}</Link>
                                        </h3>

                                        <div className="flex flex-wrap items-center gap-x-12 gap-y-4">
                                            <div className="flex items-center gap-4 group/price">
                                                <div className="w-12 h-12 rounded-2xl bg-[var(--purple)] text-white flex items-center justify-center font-black text-xl shadow-xl shadow-[var(--purple)]/20 group-hover/price:scale-110 transition-transform">
                                                    ₹
                                                </div>
                                                <span className="text-[var(--text-primary)] text-2xl font-black italic tracking-tighter">{item.price?.toLocaleString()}</span>
                                            </div>
                                            <div className="flex items-center gap-3 text-[var(--text-secondary)] font-black text-[10px] uppercase tracking-[0.2em] bg-[var(--purple)]/5 px-5 py-3 rounded-2xl border border-[var(--purple)]/10">
                                                <i className="fa-solid fa-location-crosshairs text-[var(--purple)] text-lg"></i>
                                                {item.city}{item.area && ` · ${item.area}`}
                                            </div>
                                        </div>
                                    </div>

                                    {/* Actions */}
                                    <div className="flex flex-row lg:flex-col gap-4 shrink-0 w-full lg:w-auto">
                                        <Link to={`/property-details/${item._id}`} className="flex-1 lg:w-16 lg:h-16 bg-[var(--purple)]/5 border border-[var(--purple)]/10 text-[var(--purple)] rounded-[1.5rem] flex items-center justify-center hover:bg-[var(--purple)] hover:text-white transition-all shadow-xl active:scale-90 group/btn">
                                            <i className="fa-solid fa-expand text-xl group-hover/btn:rotate-12 transition-transform"></i>
                                        </Link>
                                        <button 
                                            onClick={() => handleDelete(item._id)}
                                            disabled={deleting === item._id}
                                            className="flex-1 lg:w-16 lg:h-16 bg-rose-500/5 border border-rose-500/10 text-rose-500 rounded-[1.5rem] flex items-center justify-center hover:bg-rose-500 hover:text-white transition-all shadow-xl active:scale-90 disabled:opacity-50 group/del"
                                        >
                                            {deleting === item._id ? <i className="fa-solid fa-circle-notch fa-spin"></i> : <i className="fa-solid fa-trash-can text-xl group-hover/del:shake transition-transform"></i>}
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </UserDashboardLayout>
    );
}
